import { useState, type FormEvent, type ReactNode } from "react";

import {
  ALPHA_KEY_REGEX,
  DISPLAY_NAME_MAX_LENGTH,
  DISPLAY_NAME_MIN_LENGTH,
  PASSWORD_MIN_LENGTH,
} from "@legacy-manager/shared";

import { dataLonga, useT } from "../i18n";
import { ErroDaApi } from "../online/cliente";
import { VERSAO_DOS_TERMOS, mensagemDoErro, useSessao } from "../online/sessao";
import { useErroEmToast } from "../ui/avisos";
import { Campo } from "./Campo";
import "./porta-de-entrada.css";

/**
 * Entrar, criar conta, pedir link e redefinir senha, num formulário só.
 *
 * **Mora fora da tela de conta de propósito.** Quem ainda não entrou vê isto
 * na abertura, embaixo do emblema, e não depois de apertar um botão que leva a
 * outro lugar: entrar deixou de ser uma tela para ser o que a abertura pede.
 *
 * O arquivo separado é o que permite isso sem a abertura carregar junto as
 * seiscentas linhas de perfil, dispositivos, direitos e exclusão de conta, que
 * só servem a quem já entrou.
 *
 * As quatro portas são um formulário só porque são a mesma conversa: e-mail e
 * senha, com um campo a mais ou a menos. Separadas, cada troca perderia o
 * e-mail que a pessoa acabou de digitar.
 */

type Porta = "entrar" | "criar" | "esqueci" | "redefinir";

type NomeDoCampo = "email" | "nome" | "chaveDoAlfa" | "senha" | "codigo";

type ErrosDosCampos = Partial<Record<NomeDoCampo, string>>;

/** O nome do campo no corpo do pedido, como o servidor devolve na recusa. */
const CAMPO_DO_SERVIDOR: Record<string, NomeDoCampo> = {
  email: "email",
  displayName: "nome",
  alphaKey: "chaveDoAlfa",
  password: "senha",
  token: "codigo",
};

/**
 * A chave do alfa como ela deve aparecer, digitada ou colada de qualquer jeito.
 *
 * Tira tudo o que não é letra ou número, põe o prefixo `LFM` quando falta e os
 * hífens nos lugares certos: `lfm wpem e6jc`, `LFMWPEME6JC`, `WPEME6JC` e
 * `LFM-WPEM-E6JC` com espaço em volta viram todos `LFM-WPEM-E6JC`. Enquanto o
 * que foi digitado ainda é o começo do prefixo, fica como está, para o apagar
 * funcionar até o fim.
 */
export function formatarChaveDoAlfa(valor: string): string {
  const limpo = valor.toUpperCase().replace(/[^A-Z0-9]/g, "");
  if ("LFM".startsWith(limpo)) return limpo;
  const corpo = (limpo.startsWith("LFM") ? limpo.slice(3) : limpo).slice(0, 8);
  return corpo.length > 4 ? `LFM-${corpo.slice(0, 4)}-${corpo.slice(4)}` : `LFM-${corpo}`;
}

export function PortaDeEntrada(): ReactNode {
  const t = useT();
  const sessao = useSessao();
  const [porta, definirPorta] = useState<Porta>("entrar");
  const [email, definirEmail] = useState("");
  const [senha, definirSenha] = useState("");
  const [nome, definirNome] = useState("");
  const [codigo, definirCodigo] = useState("");
  const [chaveDoAlfa, definirChaveDoAlfa] = useState("");
  const [ocupado, definirOcupado] = useState(false);
  const [erro, definirErro] = useState<string | null>(null);
  useErroEmToast(erro);
  const [aviso, definirAviso] = useState<string | null>(null);
  const [errosDosCampos, definirErrosDosCampos] = useState<ErrosDosCampos>({});

  /** Mexer no campo tira a marca de erro dele. */
  const limparErro = (campo: NomeDoCampo): void => {
    definirErrosDosCampos((atuais) => {
      if (!(campo in atuais)) return atuais;
      const resto = { ...atuais };
      delete resto[campo];
      return resto;
    });
  };

  const rotuloDoCampo = (campo: NomeDoCampo): string => {
    if (campo === "email") return t("conta.email");
    if (campo === "nome") return t("conta.nome");
    if (campo === "chaveDoAlfa") return t("conta.chaveDoAlfa");
    if (campo === "codigo") return t("conta.codigo");
    return porta === "redefinir" ? t("conta.novaSenha") : t("conta.senha");
  };

  const motivoDoCampo = (campo: NomeDoCampo): string => {
    if (campo === "email") return t("conta.erroEmail");
    if (campo === "nome") {
      return t("conta.nomeAjuda", { minimo: DISPLAY_NAME_MIN_LENGTH, maximo: DISPLAY_NAME_MAX_LENGTH });
    }
    if (campo === "chaveDoAlfa") return t("conta.chaveDoAlfaAjuda");
    if (campo === "codigo") return t("conta.erroCodigo");
    return t("conta.senhaAjuda", { minimo: PASSWORD_MIN_LENGTH });
  };

  /** Marca os campos e diz no aviso quais são. */
  const marcarCampos = (marcados: ErrosDosCampos): void => {
    definirErrosDosCampos(marcados);
    const nomes = (Object.keys(marcados) as NomeDoCampo[]).map(rotuloDoCampo);
    definirErro(t("conta.confiraCampos", { campos: nomes.join(", ") }));
  };

  /**
   * O que o servidor recusou, campo por campo.
   *
   * A recusa de validação traz `details.campos` com o caminho de cada campo; a
   * chave que não existe e o e-mail já cadastrado têm código próprio. Nos três
   * casos o campo fica marcado, em vez de só um aviso geral no canto.
   */
  const recusar = (causa: unknown): void => {
    if (causa instanceof ErroDaApi && causa.code === "ALPHA_KEY_INVALID") {
      definirErrosDosCampos({ chaveDoAlfa: mensagemDoErro(causa) });
    } else if (causa instanceof ErroDaApi && causa.code === "EMAIL_TAKEN") {
      definirErrosDosCampos({ email: mensagemDoErro(causa) });
    } else if (causa instanceof ErroDaApi && causa.code === "VALIDATION") {
      const lista = causa.details?.["campos"];
      const marcados: ErrosDosCampos = {};
      if (Array.isArray(lista)) {
        for (const item of lista as Array<{ campo?: unknown; motivo?: unknown }>) {
          const campo = CAMPO_DO_SERVIDOR[String(item.campo ?? "").split(".")[0] ?? ""];
          if (!campo) continue;
          // Senha longa o bastante mas já vazada não é "pelo menos 10
          // caracteres": quem digitou vinte letras precisa ler o motivo certo.
          marcados[campo] =
            campo === "senha" && item.motivo === "senha vazada"
              ? t("conta.erroSenhaVazada")
              : motivoDoCampo(campo);
        }
      }
      if (Object.keys(marcados).length > 0) {
        marcarCampos(marcados);
        return;
      }
    }
    definirErro(mensagemDoErro(causa));
  };

  /** Troca de formulário sem carregar erro nem aviso do anterior. */
  const irPara = (destino: Porta): void => {
    definirErro(null);
    definirAviso(null);
    definirErrosDosCampos({});
    definirPorta(destino);
  };

  const enviar = async (evento: FormEvent): Promise<void> => {
    evento.preventDefault();
    definirErro(null);
    definirAviso(null);
    definirErrosDosCampos({});
    // Chave incompleta nem sai daqui: o servidor recusaria do mesmo jeito.
    if (porta === "criar" && !ALPHA_KEY_REGEX.test(chaveDoAlfa)) {
      marcarCampos({ chaveDoAlfa: t("conta.chaveDoAlfaAjuda") });
      return;
    }
    definirOcupado(true);
    try {
      if (porta === "entrar") {
        await sessao.entrar(email, senha);
        return;
      }
      if (porta === "criar") {
        await sessao.criarConta({ email, senha, nome, chaveDoAlfa });
        definirAviso(t("conta.contaCriada", { email }));
        definirSenha("");
        definirPorta("entrar");
        return;
      }
      if (porta === "esqueci") {
        // A resposta é sempre a mesma, exista ou não a conta: qualquer
        // diferença aqui viraria um verificador de e-mails cadastrados.
        await sessao.cliente.pedirVazio({
          metodo: "POST",
          caminho: "/v1/auth/forgot",
          corpo: { email },
          publica: true,
        });
        definirAviso(t("conta.linkEnviado"));
        definirPorta("redefinir");
        return;
      }
      await sessao.cliente.pedirVazio({
        metodo: "POST",
        caminho: "/v1/auth/reset",
        corpo: { token: codigo, password: senha },
        publica: true,
      });
      definirAviso(t("conta.senhaTrocada"));
      definirSenha("");
      definirCodigo("");
      definirPorta("entrar");
    } catch (causa) {
      recusar(causa);
    } finally {
      definirOcupado(false);
    }
  };

  const acao =
    porta === "entrar"
      ? { rotulo: t("conta.entrar"), ocupado: t("conta.entrando") }
      : porta === "criar"
        ? { rotulo: t("conta.criarConta"), ocupado: t("conta.criando") }
        : porta === "esqueci"
          ? { rotulo: t("conta.enviarLink"), ocupado: t("conta.enviando") }
          : { rotulo: t("conta.redefinir"), ocupado: t("conta.enviando") };

  return (
    <section className="porta">
      <form onSubmit={(evento) => void enviar(evento)} style={{ display: "grid", gap: 14 }}>
        {aviso && (
          <p className="porta-aviso porta-surge" role="status">
            {aviso}
          </p>
        )}

        {porta === "redefinir" ? (
          <Campo key="codigo" className="porta-surge" rotulo={t("conta.codigo")} erro={errosDosCampos.codigo}>
            <input
              value={codigo}
              onChange={(evento) => {
                limparErro("codigo");
                definirCodigo(evento.target.value);
              }}
              autoComplete="one-time-code"
              required
            />
          </Campo>
        ) : (
          <Campo key="email" className="porta-surge" rotulo={t("conta.email")} erro={errosDosCampos.email}>
            <input
              type="email"
              value={email}
              onChange={(evento) => {
                limparErro("email");
                definirEmail(evento.target.value);
              }}
              autoComplete="email"
              required
            />
          </Campo>
        )}

        {porta === "criar" && (
          <Campo
            className="porta-surge"
            rotulo={t("conta.nome")}
            ajuda={t("conta.nomeAjuda", { minimo: DISPLAY_NAME_MIN_LENGTH, maximo: DISPLAY_NAME_MAX_LENGTH })}
            erro={errosDosCampos.nome}
          >
            <input
              value={nome}
              onChange={(evento) => {
                limparErro("nome");
                definirNome(evento.target.value);
              }}
              minLength={DISPLAY_NAME_MIN_LENGTH}
              maxLength={DISPLAY_NAME_MAX_LENGTH}
              autoComplete="nickname"
              required
            />
          </Campo>
        )}

        {/*
          A chave do alfa vem depois do nome e antes da senha.

          Ela é o que decide se a conta existe, então fica no caminho de quem
          está preenchendo, e não escondida embaixo do botão. Quem não tem
          chave descobre aqui e não depois de digitar tudo.
        */}
        {porta === "criar" && (
          <Campo
            className="porta-surge"
            rotulo={t("conta.chaveDoAlfa")}
            ajuda={t("conta.chaveDoAlfaAjuda")}
            erro={errosDosCampos.chaveDoAlfa}
          >
            <input
              value={chaveDoAlfa}
              onChange={(evento) => {
                limparErro("chaveDoAlfa");
                definirChaveDoAlfa(formatarChaveDoAlfa(evento.target.value));
              }}
              placeholder="LFM-XXXX-XXXX"
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              required
              style={{ fontFamily: "var(--font-mono)", letterSpacing: "0.08em" }}
            />
          </Campo>
        )}

        {porta !== "esqueci" && (
          <Campo
            rotulo={porta === "redefinir" ? t("conta.novaSenha") : t("conta.senha")}
            ajuda={porta === "entrar" ? undefined : t("conta.senhaAjuda", { minimo: PASSWORD_MIN_LENGTH })}
            erro={errosDosCampos.senha}
          >
            <input
              type="password"
              value={senha}
              onChange={(evento) => {
                limparErro("senha");
                definirSenha(evento.target.value);
              }}
              minLength={porta === "entrar" ? undefined : PASSWORD_MIN_LENGTH}
              autoComplete={porta === "entrar" ? "current-password" : "new-password"}
              required
            />
          </Campo>
        )}

        {porta === "criar" && (
          <p className="muted porta-surge" style={{ margin: 0, fontSize: 11.5 }}>
            {/* Meio-dia em UTC para o dia não recuar em fuso a oeste de Greenwich. */}
            {t("conta.aceite", { versao: dataLonga(`${VERSAO_DOS_TERMOS}T12:00:00.000Z`) })}
          </p>
        )}

        <button className="primary" type="submit" disabled={ocupado} aria-busy={ocupado}>
          {ocupado ? acao.ocupado : acao.rotulo}
        </button>

        <div className="porta-trocas">
          {porta !== "entrar" && (
            <button type="button" className="porta-troca" onClick={() => irPara("entrar")}>
              {porta === "criar" ? t("conta.jaTenhoConta") : t("conta.voltarParaEntrar")}
            </button>
          )}
          {porta === "entrar" && (
            <>
              <button type="button" className="porta-troca" onClick={() => irPara("criar")}>
                {t("conta.naoTenhoConta")}
              </button>
              <button type="button" className="porta-troca" onClick={() => irPara("esqueci")}>
                {t("conta.esqueciASenha")}
              </button>
            </>
          )}
          {porta === "esqueci" && (
            <button type="button" className="porta-troca" onClick={() => irPara("redefinir")}>
              {t("conta.jaTenhoCodigo")}
            </button>
          )}
        </div>
      </form>
    </section>
  );
}
